import {
  futureValueLumpsum,
  futureValueStepUpSIP,
  stpFutureValue,
  monthlySWPForLumpsum,
} from "../../engine/calculations";

// Task 16.1 — the flexible one. Combine any of four accumulation legs (lumpsum, step-up SIP, and
// an STP leg) into one blended projection, then see the sustainable income that blended corpus
// could pay. Legs default to their own values; zero out a leg you don't want. Composes existing
// engine functions per year — no new engine maths.
const config = {
  key: "multipurpose",
  title: "Multipurpose Calculator",
  category: "Comprehensive & Reports",
  description: "Combine a lumpsum, a (step-up) SIP, and an STP into one blended projection, then see the income it could pay.",
  fields: [
    { key: "lumpsum", label: "One-time lumpsum", type: "currency", default: 10_00_000, min: 0, max: 50_00_00_000, help: "Set to 0 to skip this leg." },
    { key: "monthlySip", label: "Monthly SIP", type: "currency", default: 25_000, min: 0, max: 50_00_000, help: "Set to 0 to skip this leg." },
    { key: "stepUpPct", label: "Annual SIP step-up", type: "percent", default: 10, min: 0, max: 25 },
    { key: "stpSourceCorpus", label: "STP source corpus", type: "currency", default: 0, min: 0, max: 50_00_00_000, help: "A fund you'll transfer into equity gradually. Set to 0 to skip this leg." },
    { key: "stpMonthlyTransfer", label: "STP monthly transfer", type: "currency", default: 0, min: 0, max: 50_00_000 },
    { key: "sourceRate", label: "STP source fund return", type: "percent", default: 6, min: 0, max: 20 },
    { key: "returnRate", label: "Expected annual return", type: "percent", default: 12, min: 1, max: 30, assumptionKey: "equityReturn" },
    { key: "years", label: "Accumulation period (years)", type: "number", default: 20, min: 1, max: 40 },
    { key: "swpYears", label: "Then draw income for (years)", type: "number", default: 20, min: 1, max: 40 },
    { key: "swpRate", label: "Return during withdrawal phase", type: "percent", default: 8, min: 0, max: 20 },
  ],
  chart: {
    type: "line",
    xKey: "year",
    series: [
      { key: "lumpsumLeg", label: "Lumpsum", colorVar: "--muted" },
      { key: "sipLeg", label: "SIP", colorVar: "--gold" },
      { key: "stpLeg", label: "STP", colorVar: "--gold-deep" },
      { key: "total", label: "Total", colorVar: "--gold-bright" },
    ],
  },
  compute(values) {
    const lumpsum = Number(values.lumpsum) || 0;
    const monthlySip = Number(values.monthlySip) || 0;
    const stepUpPct = (Number(values.stepUpPct) || 0) / 100;
    const stpSourceCorpus = Number(values.stpSourceCorpus) || 0;
    const stpMonthlyTransfer = Number(values.stpMonthlyTransfer) || 0;
    const sourceRate = (Number(values.sourceRate) || 0) / 100;
    const annualRate = (Number(values.returnRate) || 0) / 100;
    const years = Number(values.years) || 0;
    const swpYears = Number(values.swpYears) || 0;
    const swpRate = (Number(values.swpRate) || 0) / 100;

    const legValue = (y) => {
      const lumpsumLeg = futureValueLumpsum(lumpsum, annualRate, y);
      const sipLeg = futureValueStepUpSIP(monthlySip, annualRate, y, stepUpPct);
      const stp = stpFutureValue(stpSourceCorpus, sourceRate, annualRate, stpMonthlyTransfer, y * 12);
      const stpLeg = stp.finalSource + stp.finalTarget;
      return { lumpsumLeg, sipLeg, stpLeg, total: lumpsumLeg + sipLeg + stpLeg };
    };

    const final = legValue(years);
    const sustainableSwp = monthlySWPForLumpsum(final.total, swpRate, swpYears);

    const series = [];
    const rows = [];
    for (let y = 1; y <= years; y++) {
      const v = legValue(y);
      series.push({ year: y, ...v });
      rows.push({ year: y, ...v });
    }

    return {
      headline: { label: "Blended corpus", value: final.total },
      extraHeadlines: [
        { label: "From lumpsum", value: final.lumpsumLeg },
        { label: "From SIP", value: final.sipLeg },
        { label: "From STP", value: final.stpLeg },
        { label: `Sustainable monthly income for ${swpYears}y`, value: sustainableSwp },
      ],
      breakdown: [
        { label: "Accumulation period", value: `${years} years`, format: "text" },
      ],
      series,
      table: {
        title: "Year-by-year blended value",
        columns: [
          { key: "year", label: "Year", format: "text" },
          { key: "lumpsumLeg", label: "Lumpsum" },
          { key: "sipLeg", label: "SIP" },
          { key: "stpLeg", label: "STP" },
          { key: "total", label: "Total" },
        ],
        rows,
      },
    };
  },
};

export default config;
